import { useState } from "react";
import { Crown, Check, Trophy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

const TIERS = [
  { icon: "🦍", name: "Tier 1 – Recruit", sales: "$1,000", rate: "10%", reward: "Personal affiliate link + dashboard access" },
  { icon: "🦍", name: "Tier 2 – Alpha", sales: "$2,500", rate: "12%", reward: "Ape-Tides apparel package" },
  { icon: "🦍", name: "Tier 3 – Silverback", sales: "$5,000", rate: "15%", reward: "$100 Ape-Tides credit" },
  { icon: "🦍", name: "Tier 4 – Titan", sales: "$10,000", rate: "18%", reward: "Exclusive product drops + early access" },
  { icon: "🦍", name: "Tier 5 – Elite", sales: "$25,000", rate: "20%", reward: "4-day Ape-Tides sponsored vacation + $500 cash bonus" },
  { icon: "🦍", name: "Tier 6 – Apex", sales: "$50,000", rate: "22%", reward: "$1,500 cash bonus + featured on Ape-Tides social media" },
  { icon: "🦍", name: "Tier 7 – King Kong", sales: "$75,000", rate: "25%", reward: "$3,000 cash bonus + VIP recognition" },
  { icon: "👑", name: "Tier 8 – Legend", sales: "$100,000+", rate: "30%", reward: "$5,000 cash bonus + lifetime 30% commission + custom Ape-Tides jacket + Hall of Fame recognition", legend: true },
];

const CONTENT_TYPES = ["Fitness / Bodybuilding", "Health & Wellness", "Lifestyle", "Coaching", "Other"];

const Field = ({ label, textarea, ...p }) => (
  <label className="block">
    <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">{label.toUpperCase()}</span>
    {textarea
      ? <textarea {...p} rows={4} className="w-full bg-transparent border-2 border-ape-border rounded-none p-3 mt-1 text-white focus:border-ape-red focus:outline-none transition-colors duration-150" />
      : <input {...p} className="w-full bg-transparent border-b-2 border-ape-border rounded-none py-2 text-white focus:border-ape-red focus:outline-none transition-colors duration-150" />}
  </label>
);

export default function Affiliate() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", social: "", location: "", content_types: [], other: "", about: "", affiliate_code: "", signature: "", agreed: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleContent = (c) => setForm((f) => ({ ...f, content_types: f.content_types.includes(c) ? f.content_types.filter((x) => x !== c) : [...f.content_types, c] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed) { toast.error("Please accept the affiliate agreement."); return; }
    setLoading(true);
    try {
      const types = form.content_types.map((c) => c === "Other" && form.other ? `Other: ${form.other}` : c);
      const { data } = await api.post("/affiliate/apply", {
        full_name: form.full_name, email: form.email, phone: form.phone, social: form.social,
        location: form.location, content_types: types, about: form.about,
        affiliate_code: form.affiliate_code, signature: form.signature, agreed: form.agreed,
      });
      toast.success(data.message);
      setDone(true);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-ape-border overflow-hidden">
        <img src={BRAND.workout} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-ape-bg via-ape-bg/85 to-ape-bg/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24">
          <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">🦍 APE-TIDES AFFILIATE PROGRAM</span>
          <h1 className="font-display text-5xl sm:text-7xl tracking-tight mt-3 max-w-3xl">JOIN THE<br /><span className="text-ape-red">MOVEMENT.</span></h1>
          <p className="text-ape-muted text-lg mt-5 max-w-2xl">Help grow the Ape-Tides community while earning commissions, rewards, and exclusive benefits. Climb the ranks — from Recruit to Legend.</p>
        </div>
      </section>

      {/* TIERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="mb-12">
          <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">COMMISSION TIERS</span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">CLIMB THE RANKS</h2>
        </div>
        <div className="grid gap-4">
          {TIERS.map((t, i) => (
            <div key={i} data-testid={`tier-${i + 1}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-4 items-center border p-6 ${t.legend ? "border-ape-red bg-ape-red/10" : "border-ape-border bg-ape-surface"}`}>
              <div className="md:col-span-4 flex items-center gap-3">
                <span className="text-2xl">{t.icon}</span>
                <span className="font-display text-2xl tracking-wide">{t.name}</span>
              </div>
              <div className="md:col-span-2">
                <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">SALES VOLUME</div>
                <div className="font-mono2 text-lg font-bold">{t.sales}</div>
              </div>
              <div className="md:col-span-2">
                <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">COMMISSION</div>
                <div className={`font-display text-3xl ${t.legend ? "text-ape-red" : "text-white"}`}>{t.rate}</div>
              </div>
              <div className="md:col-span-4">
                <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">REWARD</div>
                <div className="text-sm mt-0.5">{t.reward}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* APPLICATION */}
      <section className="border-t border-ape-border bg-ape-surface">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
          <div className="text-center mb-10">
            <Trophy className="mx-auto text-ape-red mb-4" size={44} />
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight">🦍 AFFILIATE APPLICATION</h2>
            <p className="text-ape-muted mt-3">Join the movement. Earn commissions, rewards, and exclusive benefits.</p>
          </div>

          {done ? (
            <div className="border border-ape-red bg-ape-red/10 p-10 text-center" data-testid="affiliate-success">
              <Check className="mx-auto text-ape-red mb-4" size={44} />
              <h3 className="font-display text-3xl tracking-tight">APPLICATION SUBMITTED</h3>
              <p className="text-ape-muted mt-3">Welcome to the movement. Our team will review your application and reach out shortly with your affiliate dashboard access.</p>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-10" data-testid="affiliate-form">
            {/* Personal */}
            <div>
              <h3 className="font-head uppercase tracking-widest text-ape-red text-sm mb-5 border-b border-ape-border pb-2">Personal Information</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" required value={form.full_name} onChange={set("full_name")} data-testid="aff-name" />
                <Field label="Email Address" type="email" required value={form.email} onChange={set("email")} data-testid="aff-email" />
                <Field label="Phone Number" value={form.phone} onChange={set("phone")} data-testid="aff-phone" />
                <Field label="Instagram / Social Handles" value={form.social} onChange={set("social")} data-testid="aff-social" />
                <div className="sm:col-span-2"><Field label="Location" value={form.location} onChange={set("location")} data-testid="aff-location" /></div>
              </div>
            </div>

            {/* About */}
            <div>
              <h3 className="font-head uppercase tracking-widest text-ape-red text-sm mb-5 border-b border-ape-border pb-2">About You</h3>
              <div className="font-mono2 text-[10px] tracking-widest text-ape-muted mb-3">WHAT TYPE OF CONTENT DO YOU CREATE?</div>
              <div className="flex flex-wrap gap-3 mb-3">
                {CONTENT_TYPES.map((c) => (
                  <button type="button" key={c} onClick={() => toggleContent(c)} data-testid={`aff-content-${c.split(" ")[0].toLowerCase()}`}
                    className={`font-head uppercase tracking-wide text-xs px-4 py-2.5 border transition-colors duration-150 ${form.content_types.includes(c) ? "bg-ape-red border-ape-red text-white" : "border-ape-border text-ape-muted hover:text-white hover:border-white"}`}>
                    {form.content_types.includes(c) && "✓ "}{c}
                  </button>
                ))}
              </div>
              {form.content_types.includes("Other") && (
                <Field label="Other (specify)" value={form.other} onChange={set("other")} data-testid="aff-other" />
              )}
              <div className="mt-5"><Field label="Tell us about your audience and why you'd be a good fit" textarea value={form.about} onChange={set("about")} data-testid="aff-about" /></div>
            </div>

            {/* Code */}
            <div>
              <h3 className="font-head uppercase tracking-widest text-ape-red text-sm mb-5 border-b border-ape-border pb-2">Affiliate Code Setup</h3>
              <p className="text-ape-muted text-sm mb-4">Your code lets members receive discounts while allowing Ape-Tides to track your sales and commissions. Examples: <span className="font-mono2 text-white">BEAST10</span>, <span className="font-mono2 text-white">SILVERBACK15</span>, <span className="font-mono2 text-white">GORILLA20</span></p>
              <Field label="Requested Affiliate Code" required value={form.affiliate_code} onChange={(e) => setForm({ ...form, affiliate_code: e.target.value.toUpperCase() })} data-testid="aff-code" placeholder="BEAST10" />
              <p className="text-ape-muted text-xs mt-2 font-mono2">(Ape-Tides reserves the right to approve or modify affiliate codes.)</p>
            </div>

            {/* Agreement */}
            <div>
              <h3 className="font-head uppercase tracking-widest text-ape-red text-sm mb-5 border-b border-ape-border pb-2">Agreement</h3>
              <p className="text-sm text-ape-muted mb-3">By submitting this application, I agree to:</p>
              <ul className="space-y-2 text-sm mb-5">
                {["Represent Ape-Tides professionally", "Follow Ape-Tides marketing guidelines", "Not make false claims about products", "Use my assigned affiliate code for tracking sales", "Understand commissions are based on verified sales generated through my affiliate code"].map((t, i) => (
                  <li key={i} className="flex items-center gap-2"><Check size={16} className="text-ape-red shrink-0" /> {t}</li>
                ))}
              </ul>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Signature (type full name)" required value={form.signature} onChange={set("signature")} data-testid="aff-signature" />
                <div>
                  <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">DATE</span>
                  <div className="border-b-2 border-ape-border py-2 font-mono2 text-white">{new Date().toLocaleDateString()}</div>
                </div>
              </div>
              <label className="flex items-center gap-3 mt-6 cursor-pointer">
                <input type="checkbox" checked={form.agreed} onChange={(e) => setForm({ ...form, agreed: e.target.checked })} data-testid="aff-agree" className="w-5 h-5 accent-[#E30613]" />
                <span className="text-sm">I have read and accept the Ape-Tides affiliate agreement.</span>
              </label>
            </div>

            <button disabled={loading} className="w-full bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white font-head uppercase tracking-widest py-4 transition-colors duration-150" data-testid="aff-submit">
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </form>
          )}
        </div>
      </section>
    </div>
  );
}
